import React, { useState } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Text,
    Button,
    useColorModeValue,
    Divider,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useToast,
    Badge,
} from '@chakra-ui/react';
import {
    FiCheck,
    FiX,
    FiRefreshCw,
} from 'react-icons/fi';
import { useAuth } from '../../auth/AuthContext';
import { useLoginRequests } from '../hooks/useLoginRequests';
import LoginRequestStatsCards from './LoginRequestStatsCards';
import LoginRequestFilterBar from './LoginRequestFilterBar';
import LoginRequestTable from './LoginRequestTable';
import LoginRequestPagination from './LoginRequestPagination';
import { ADMIN_THEME_CONFIG } from '../config';
import { trackDownload } from '../../activity';

const LoginRequestManagement: React.FC = () => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const { isOpen: isBulkOpen, onOpen: onBulkOpen, onClose: onBulkClose } = useDisclosure();
    const [bulkAction, setBulkAction] = useState<'approve' | 'reject'>('approve');

    const { user } = useAuth();
    const toast = useToast();

    const {
        requests,
        loading,
        error,
        pagination,
        filters,
        stats,
        handleApprove,
        handleReject,
        handleBulkApprove,
        handleBulkReject,
        handlePageChange,
        handleFilterChange,
        refetch,
    } = useLoginRequests();

    const bg = useColorModeValue('gray.50', 'gray.900');
    const textColor = useColorModeValue('gray.800', 'gray.100');

    const currentUser = user?.email || 'admin@gbs.id';

    // Export to CSV function
    const handleExportCSV = () => {
        const headers = [
            'ID',
            'NIP',
            'Employee Name',
            'Device ID',
            'Device Model',
            'Status',
            'Request Date',
            'Approved Date',
            'Approved By'
        ];

        const csvData = requests.map(request => [
            request.id,
            request.nip,
            request.employee_name || '',
            request.device_id,
            request.device_model,
            request.status,
            new Date(request.created_at).toLocaleDateString('id-ID'),
            request.approved_at ? new Date(request.approved_at).toLocaleDateString('id-ID') : '',
            request.approved_by || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.map(field => `"${field}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `login-requests-${new Date().toISOString().split('T')[0]}.csv`);
        trackDownload('Login Request');
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
            title: 'Export Successful',
            description: 'CSV file has been downloaded',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    };

    const handleBulkOperation = async (action: 'approve' | 'reject') => {
        if (selectedIds.length === 0) {
            toast({
                title: 'No Selection',
                description: 'Please select requests to process',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setBulkAction(action);
        onBulkOpen();
    };

    const confirmBulkOperation = async () => {
        try {
            if (bulkAction === 'approve') {
                await handleBulkApprove(selectedIds, currentUser);
            } else {
                await handleBulkReject(selectedIds, currentUser);
            }

            setSelectedIds([]);
            onBulkClose();
        } catch (error) {
            console.error('Bulk operation error:', error);
        }
    };

    return (
        <Box minH="100vh" bg={bg} p={6}>
            <VStack align="stretch" spacing={6}>
                {/* Header */}
                <Box>
                    <HStack justify="space-between" align="start" mb={2}>
                        <VStack align="start" spacing={1}>
                            <Heading size="lg" color={ADMIN_THEME_CONFIG.COLORS.PRIMARY}>
                                Login Request Management
                            </Heading>
                            <Text color={textColor} fontSize="md">
                                Kelola permintaan akses login dari aplikasi mobile
                            </Text>
                        </VStack>

                        <HStack spacing={2}>
                            <Button
                                leftIcon={<FiRefreshCw />}
                                onClick={refetch}
                                isLoading={loading}
                                variant="outline"
                                size="sm"
                            >
                                Refresh
                            </Button>
                        </HStack>
                    </HStack>

                    <Divider borderColor={useColorModeValue('gray.200', 'gray.700')} />
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <AlertTitle>Error!</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Stats Cards */}
                <LoginRequestStatsCards stats={stats} loading={loading} />

                {/* Bulk Actions Bar */}
                {selectedIds.length > 0 && (
                    <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <VStack align="start" spacing={2} flex={1}>
                            <HStack>
                                <Text fontWeight="medium">
                                    {selectedIds.length} request(s) selected
                                </Text>
                                <Badge colorScheme="blue">{selectedIds.length}</Badge>
                            </HStack>
                            <HStack spacing={2}>
                                <Button
                                    size="sm"
                                    colorScheme="green"
                                    leftIcon={<FiCheck />}
                                    onClick={() => handleBulkOperation('approve')}
                                >
                                    Bulk Approve
                                </Button>
                                <Button
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    leftIcon={<FiX />}
                                    onClick={() => handleBulkOperation('reject')}
                                >
                                    Bulk Reject
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setSelectedIds([])}
                                >
                                    Clear Selection
                                </Button>
                            </HStack>
                        </VStack>
                    </Alert>
                )}

                {/* Filter Bar */}
                <LoginRequestFilterBar
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onRefresh={refetch}
                    onExport={handleExportCSV}
                    loading={loading}
                    totalRecords={pagination.total}
                />

                {/* Table */}
                <LoginRequestTable
                    requests={requests}
                    loading={loading}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    selectedIds={selectedIds}
                    onSelectionChange={setSelectedIds}
                    currentUser={currentUser}
                />

                {/* Pagination */}
                <LoginRequestPagination
                    currentPage={pagination.current}
                    pageSize={pagination.pageSize}
                    totalItems={pagination.total}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    loading={loading}
                />

                {/* Bulk Action Confirmation Modal */}
                <Modal isOpen={isBulkOpen} onClose={onBulkClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>
                            Confirm Bulk {bulkAction === 'approve' ? 'Approval' : 'Rejection'}
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack align="start" spacing={3}>
                                <Text>
                                    Are you sure you want to {bulkAction} {selectedIds.length} login request(s)?
                                </Text>
                                <Alert status={bulkAction === 'approve' ? 'success' : 'warning'} size="sm">
                                    <AlertIcon />
                                    This action cannot be undone.
                                </Alert>
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onBulkClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme={bulkAction === 'approve' ? 'green' : 'red'}
                                onClick={confirmBulkOperation}
                            >
                                {bulkAction === 'approve' ? 'Approve All' : 'Reject All'}
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    );
};

export default LoginRequestManagement;
